import siteService from '../../services/siteService';

import designServices, {

    type RetrieveSystemDesignResponse,

} from '../../services/oneButtonServices/designServices';

import { useOneButtonStore } from '../../store/oneButtonStore';

import {

    resolveEnergyCommunityLikelyQualifies,

} from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/EnergyCommunityNotice';

import type { PreContractIntakeForm } from './preContractTypes';

import { hydrateOpportunityStore } from './preContractTitle';



export type PreContractProductPrefill = {

    Solar: boolean;

    Roof: boolean;

    Battery: boolean;

};



function asRecord(value: unknown): Record<string, unknown> | null {

    return value && typeof value === 'object' && !Array.isArray(value)

        ? (value as Record<string, unknown>)

        : null;

}



function readProductRows(opportunity: unknown): Record<string, unknown>[] {

    const row = asRecord(opportunity);

    if (!row) return [];

    const raw =

        row.OpportunityProduct ??

        row.opportunityProduct ??

        row.OpportunityProductsFinanceInfo ??

        [];

    return Array.isArray(raw) ? raw.map((item) => asRecord(item)).filter(Boolean) as Record<string, unknown>[] : [];

}



function isActiveProduct(row: Record<string, unknown>): boolean {

    return row.ActiveFlag !== false && row.activeFlag !== false;

}



function productTypeName(row: Record<string, unknown>): string {

    return String(row.Product_Type_Name ?? row.product_Type_Name ?? '').trim();

}



export function resolveProductPrefillFromOpportunity(

    opportunity: unknown,

): PreContractProductPrefill | null {

    const row = asRecord(opportunity);

    if (!row) return null;



    const interest = asRecord(row.OpportunityInterest) ?? asRecord(row.opportunityInterest);

    const activeRows = readProductRows(opportunity).filter(isActiveProduct);



    const fromInterest: PreContractProductPrefill = {

        Solar: interest?.Solar === true,

        Roof: interest?.Roof === true || interest?.RoofOnly === true || interest?.RRR === true,

        Battery: interest?.Battery === true || interest?.BatteryOnly === true,

    };



    const fromProducts: PreContractProductPrefill = {

        Solar: activeRows.some((item) => productTypeName(item) === 'Solar'),

        Roof: activeRows.some((item) => productTypeName(item) === 'Roof'),

        Battery: activeRows.some((item) => productTypeName(item) === 'Battery'),

    };



    const merged: PreContractProductPrefill = {

        Solar: fromInterest.Solar || fromProducts.Solar,

        Roof: fromInterest.Roof || fromProducts.Roof,

        Battery: fromInterest.Battery || fromProducts.Battery,

    };



    if (!merged.Solar && !merged.Roof && !merged.Battery) return null;

    return merged;

}



export function hasProductPrefill(prefill: PreContractProductPrefill | null): boolean {

    if (!prefill) return false;

    return prefill.Solar || prefill.Roof || prefill.Battery;

}



export function mergeProductPrefill(

    form: PreContractIntakeForm,

    prefill: PreContractProductPrefill,

    options?: { replacePrimaryProducts?: boolean },

): Partial<PreContractIntakeForm> {

    if (options?.replacePrimaryProducts) {

        const nextProducts = {

            ...form.products,

            Solar: prefill.Solar,

            Roof: prefill.Roof,

            Battery: prefill.Battery,

            RR: false,

            RRR: false,

        };

        if (

            nextProducts.Solar === form.products.Solar &&

            nextProducts.Roof === form.products.Roof &&

            nextProducts.Battery === form.products.Battery

        ) {

            return {};

        }

        return { products: nextProducts };

    }



    const nextProducts = { ...form.products };

    let changed = false;

    (['Solar', 'Roof', 'Battery'] as const).forEach((product) => {

        if (prefill[product] && !nextProducts[product]) {

            nextProducts[product] = true;

            changed = true;

        }

    });

    return changed ? { products: nextProducts } : {};

}



export function mergeBatteryPrefillFromSystemDesign(

    form: PreContractIntakeForm,

    systemDesign: RetrieveSystemDesignResponse | null,

): Partial<PreContractIntakeForm> {

    if (!form.products.Battery || !systemDesign) return {};



    const batteryInfo = asRecord(systemDesign.BatteryInformation);

    const patch: Partial<PreContractIntakeForm> = {};

    let changed = false;



    const manufacturer = String(batteryInfo?.Manufacturer ?? '').trim();

    const name = String(batteryInfo?.Name ?? '').trim();

    const quantityRaw = batteryInfo?.Quantity;

    const quantity =

        typeof quantityRaw === 'number'

            ? quantityRaw

            : Number.parseInt(String(quantityRaw ?? ''), 10);



    if (!form.batteryManufacturer.trim() && manufacturer) {

        patch.batteryManufacturer = manufacturer;

        changed = true;

    }

    if (!form.batteryName.trim() && name) {

        patch.batteryName = name;

        changed = true;

    }

    if (form.batteryQuantity <= 0 && Number.isFinite(quantity) && quantity > 0) {

        patch.batteryQuantity = quantity;

        changed = true;

    }



    return changed ? patch : {};

}



/** Same EC lookup oneDRAW uses when loading a site (`siteService.getOpportunity`). */

export async function fetchAndStoreEnergyCommunityStatus(

    salesForceId: string,

): Promise<boolean> {

    const sfid = salesForceId.trim();

    const store = useOneButtonStore.getState();

    if (!sfid) {

        return resolveEnergyCommunityLikelyQualifies(

            store.opportunityDetails,

            store.energyCommunityStatus,

        );

    }



    try {

        const ecResponse = await siteService.getOpportunity(sfid);

        const statusRaw = ecResponse?.EnergyCommunityStatus;

        const status =

            statusRaw != null && String(statusRaw).trim() !== ''

                ? String(statusRaw).trim()

                : null;



        store.setEnergyCommunityStatus(status);



        const latest = useOneButtonStore.getState();

        if (status && latest.opportunityDetails) {

            latest.setOpportunityDetails({

                ...latest.opportunityDetails,

                EnergyCommunityStatus: status,

            });

        }



        return resolveEnergyCommunityLikelyQualifies(

            useOneButtonStore.getState().opportunityDetails,

            status,

        );

    } catch {

        return resolveEnergyCommunityLikelyQualifies(

            useOneButtonStore.getState().opportunityDetails,

            useOneButtonStore.getState().energyCommunityStatus,

        );

    }

}



/** @deprecated Use {@link fetchAndStoreEnergyCommunityStatus}. */

export async function hydrateEnergyCommunityOnOpportunity(

    salesForceId: string,

): Promise<boolean> {

    return fetchAndStoreEnergyCommunityStatus(salesForceId);

}



export async function fetchOpportunityPrefillContext(salesForceId: string): Promise<{

    productPrefill: PreContractProductPrefill | null;

    energyCommunityLikelyQualifies: boolean;

}> {

    const sfid = salesForceId.trim();

    if (!sfid) {

        return { productPrefill: null, energyCommunityLikelyQualifies: false };

    }



    await hydrateOpportunityStore(sfid);

    const energyCommunityLikelyQualifies = await fetchAndStoreEnergyCommunityStatus(sfid);

    const productPrefill = resolveProductPrefillFromOpportunity(

        useOneButtonStore.getState().opportunityDetails,

    );



    return { productPrefill, energyCommunityLikelyQualifies };

}



export async function ensureSystemDesignForPrefill(

    salesForceId: string,

): Promise<RetrieveSystemDesignResponse | null> {

    const existing = useOneButtonStore.getState().systemDesign;

    if (existing) return existing;



    const oppId = useOneButtonStore.getState().opportunityID;

    if (!oppId) {

        await hydrateOpportunityStore(salesForceId);

    }



    const resolvedId = useOneButtonStore.getState().opportunityID;

    if (!resolvedId) return null;



    try {

        const design = await designServices.RetrieveSystemDesign(resolvedId);

        if (design) {

            useOneButtonStore.getState().setSystemDesign(design);

        }

        return design ?? null;

    } catch {

        return null;

    }

}

